import {createAsyncThunk} from '@reduxjs/toolkit'
import axios from 'axios'
import {setupAxios} from '../../setup-axios'
import apiHealthChecker from '../../../utils/apiHealthCheck'
import circuitBreaker from '../../../utils/circuitBreaker'
import {
  FetchChatbotResponsesRequest,
  FetchActiveResponsesRequest,
  CreateChatbotResponseRequest,
  UpdateChatbotResponseRequest,
  DeleteChatbotResponseRequest,
  ToggleChatbotResponseStatusRequest,
  SearchChatbotResponsesRequest,
  FetchChatbotResponsesResponse,
  FetchActiveResponsesResponse,
  CreateChatbotResponseResponse,
  UpdateChatbotResponseResponse,
  DeleteChatbotResponseResponse,
  ToggleChatbotResponseStatusResponse,
  SearchChatbotResponsesResponse,
} from './types'

// Ensure axios is properly configured with auth headers
setupAxios(axios)

const API_URL = process.env.REACT_APP_API_URL || 'https://api.maarefati.com'
const DEBUG_CHATBOT = process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEBUG_CHATBOT === 'true'

// Fetch paginated chatbot responses
export const fetchChatbotResponses = createAsyncThunk<
  FetchChatbotResponsesResponse,
  FetchChatbotResponsesRequest
>(
  'chatbot/fetchResponses',
  async (params, {rejectWithValue}) => {
    try {
      const queryParams = new URLSearchParams()
      
      if (params.search && params.search !== undefined && params.search.trim()) queryParams.append('search', params.search)
      if (params.category && params.category !== undefined && params.category.trim()) queryParams.append('category', params.category)
      if (params.language && params.language !== undefined && params.language.trim()) queryParams.append('language', params.language)
      if (params.isActive !== undefined && params.isActive !== '' && params.isActive !== null) queryParams.append('isActive', String(params.isActive))
      queryParams.append('pageNumber', String(params.pageNumber))
      queryParams.append('pageCount', String(params.pageCount))
      queryParams.append('sortField', params.sortField)
      queryParams.append('sortOrder', params.sortOrder)

      const url = `${API_URL}/api/chatbot/responses?${queryParams}`
      
      const response = await axios.get(url)
      return response.data
    } catch (error: any) {
      console.error('Error fetching chatbot responses:', error)
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch chatbot responses'
      )
    }
  }
)

// Fetch active chatbot responses
export const fetchActiveChatbotResponses = createAsyncThunk<
  FetchActiveResponsesResponse,
  FetchActiveResponsesRequest
>(
  'chatbot/fetchActiveResponses',
  async (params, {rejectWithValue}) => {
    try {
      // Check circuit breaker first
      if (circuitBreaker.isOpen()) {
        return {
          success: true,
          data: {
            responses: [],
            fallback: true
          }
        }
      }

      // Check API health before making request
      const isApiHealthy = await apiHealthChecker.checkApiHealth()
      if (!isApiHealthy) {
        return {
          success: true,
          data: {
            responses: [],
            fallback: true
          }
        }
      }
      
      // Check if token exists
      const auth_token_key = process.env.REACT_APP_AUTH_TOKEN_KEY!
      const token = localStorage.getItem(auth_token_key)

      // For pre-login users, try to fetch actual data from your database
      if (!token && params.category === 'pre-login') {
        try {
          const preLoginUrl = `${API_URL}/api/chatbot/responses?category=pre-login&language=${params.language}&isActive=true&pageNumber=1&pageCount=50&sortField=priority&sortOrder=desc`
          
          const response = await circuitBreaker.execute(async () => {
            return await axios.get(preLoginUrl)
          })
          if (response.data && response.data.success && response.data.data && response.data.data.responses && response.data.data.responses.length > 0) {
            return {
              success: true,
              data: {
                responses: response.data.data.responses
              }
            }
          }
        } catch {
          try {
            const altUrl = `${API_URL}/api/chatbot/public-responses?category=pre-login&language=${params.language}`
            const altResponse = await circuitBreaker.execute(async () => {
              return await axios.get(altUrl)
            })
            if (altResponse.data && altResponse.data.success && altResponse.data.data && altResponse.data.data.responses && altResponse.data.data.responses.length > 0) {
              return {
                success: true,
                data: {
                  responses: altResponse.data.data.responses
                }
              }
            }
          } catch {
            // Fall through to fallback below
          }
        }
        
        // Function to get translated responses based on language
        const getTranslatedResponses = (language: string) => {
          const translations: { [key: string]: { [key: string]: string } } = {
            'en': {
              'MAIN_PARTNERS': "El Farabi Private School is located in Tinja, Bizerte. We provide high-quality primary education in a safe and innovative environment. Address: Rue Sbitla, Tinja, Bizerte.",
              'WHAT_IS_MAAREFATI': "Maarefati is a smart digital platform that simplifies teaching, learning, and school management. Visit maarefati.com to explore how it connects students, teachers, and parents.",
              'HOW_TO_GET_STARTED': "To get started, click on 'Register' and create your account. Select your role (student, parent, or teacher) and follow the steps. Once registered, you can access your personalized dashboard.",
              'WHAT_SERVICES_OFFER': "Maarefati provides: 📘 Student learning portals, 👨‍👩‍👧 Parent dashboards, 🏫 School administration tools, and 📊 Academic tracking systems. All designed to improve the learning experience.",
              'HOW_CREATE_ACCOUNT': "Click on the 'Create Account' button, fill in your details, and choose your role. It only takes a few minutes to set up your profile and access the platform.",
              'HOW_CONTACT_SUPPORT': "Our support team is ready to help you via: 📧 Email: support@maarefati.com ☎️ Phone: +216 92 329 007 📄 Online form at maarefati.com/contact.",
              'WHAT_LANGUAGES_SUPPORTED': "Maarefati supports Arabic, French, and English interfaces to make it easy for everyone to use.",
              'WHO_CAN_USE_MAAREFATI': "The platform is designed for: 👩‍🏫 Teachers (to manage classes), 👨‍👩‍👧 Parents (to follow children's progress), and 👦 Students (to access learning resources).",
              'MOBILE_ACCESS': "Yes! Maarefati is mobile-friendly and can be accessed on smartphones, tablets, and computers—anytime, anywhere.",
              'WHY_CHOOSE_MAAREFATI': "Because it saves time ⏳, improves communication 📢, supports students 📚, and gives schools modern digital management tools 🏫.",
              'HOW_JOIN_FAMILY': "To join Maarefati, you need to request an access token (6-digit code). You can get it by: 📧 Sending an email to ecolepriveeelfarabi@gmail.com ☎️ Calling the school at +216 92 329 007 🏫 Visiting the school establishment directly. Once you have your token, you can proceed with registration.",
              'HOW_REGISTER': "📝 Registration steps:\n1️⃣ Check your email for the 6-digit access code.\n2️⃣ Copy the code and paste it into the signup form on Maarefati.\n3️⃣ Complete the registration procedure.\n4️⃣ You will receive a confirmation email.\n✅ Welcome to the Maarefati community!",
              'HOW_DOWNLOAD_APP': "📲 Maarefati can be installed as a web app (PWA). Steps:\n- Open Google Chrome (Android) or Safari (iOS).\n- Visit maarefati.com.\n- Select **Add to Home Screen** from the browser options.\nYou'll now have the app icon on your phone like any other application."
            },
            'fr': {
              'MAIN_PARTNERS': "L'École Privée El Farabi est située à Tinja, Bizerte. Nous offrons une éducation primaire de haute qualité dans un environnement sûr et innovant. Adresse : Rue Sbitla, Tinja, Bizerte.",
              'WHAT_IS_MAAREFATI': "Maarefati est une plateforme numérique intelligente qui simplifie l'enseignement, l'apprentissage et la gestion scolaire. Visitez maarefati.com pour découvrir comment elle connecte les étudiants, les enseignants et les parents.",
              'HOW_TO_GET_STARTED': "Pour commencer, cliquez sur 'S'inscrire' et créez votre compte. Sélectionnez votre rôle (étudiant, parent ou enseignant) et suivez les étapes. Une fois inscrit, vous pouvez accéder à votre tableau de bord personnalisé.",
              'WHAT_SERVICES_OFFER': "Maarefati fournit : 📘 Portails d'apprentissage étudiants, 👨‍👩‍👧 Tableaux de bord parents, 🏫 Outils d'administration scolaire, et 📊 Systèmes de suivi académique. Tout conçu pour améliorer l'expérience d'apprentissage.",
              'HOW_CREATE_ACCOUNT': "Cliquez sur le bouton 'Créer un compte', remplissez vos détails et choisissez votre rôle. Il ne faut que quelques minutes pour configurer votre profil et accéder à la plateforme.",
              'HOW_CONTACT_SUPPORT': "Notre équipe de support est prête à vous aider via : 📧 Email : support@maarefati.com ☎️ Téléphone : +216 92 329 007 📄 Formulaire en ligne sur maarefati.com/contact.",
              'WHAT_LANGUAGES_SUPPORTED': "Maarefati prend en charge les interfaces arabe, française et anglaise pour faciliter l'utilisation par tous.",
              'WHO_CAN_USE_MAAREFATI': "La plateforme est conçue pour : 👩‍🏫 Enseignants (pour gérer les classes), 👨‍👩‍👧 Parents (pour suivre les progrès des enfants), et 👦 Étudiants (pour accéder aux ressources d'apprentissage).",
              'MOBILE_ACCESS': "Oui ! Maarefati est compatible mobile et peut être consulté sur smartphones, tablettes et ordinateurs—à tout moment, n'importe où.",
              'WHY_CHOOSE_MAAREFATI': "Parce qu'il fait gagner du temps ⏳, améliore la communication 📢, soutient les étudiants 📚, et donne aux écoles des outils modernes de gestion numérique 🏫.",
              'HOW_JOIN_FAMILY': "Pour rejoindre Maarefati, vous devez demander un jeton d'accès (code à 6 chiffres). Vous pouvez l'obtenir en : 📧 Envoyant un email à ecolepriveeelfarabi@gmail.com ☎️ Appelant l'école au +216 92 329 007 🏫 Visitant directement l'établissement scolaire. Une fois que vous avez votre jeton, vous pouvez procéder à l'inscription.",
              'HOW_REGISTER': "📝 Étapes d'inscription :\n1️⃣ Vérifiez votre email pour le code d'accès à 6 chiffres.\n2️⃣ Copiez le code et collez-le dans le formulaire d'inscription sur Maarefati.\n3️⃣ Complétez la procédure d'inscription.\n4️⃣ Vous recevrez un email de confirmation.\n✅ Bienvenue dans la communauté Maarefati !",
              'HOW_DOWNLOAD_APP': "📲 Maarefati peut être installé comme une application web (PWA). Étapes :\n- Ouvrez Google Chrome (Android) ou Safari (iOS).\n- Visitez maarefati.com.\n- Sélectionnez **Ajouter à l'écran d'accueil** dans les options du navigateur.\nVous aurez maintenant l'icône de l'application sur votre téléphone comme toute autre application."
            },
            'ar': {
              'MAIN_PARTNERS': "مدرسة الفاربي الخاصة تقع في تنجة، بنزرت. نحن نقدم تعليماً ابتدائياً عالي الجودة في بيئة آمنة ومبتكرة. العنوان: شارع سبيطلة، تنجة، بنزرت.",
              'WHAT_IS_MAAREFATI': "معارفتي هي منصة رقمية ذكية تبسط التدريس والتعلم وإدارة المدرسة. زر maarefati.com لاستكشاف كيف تربط الطلاب والمعلمين وأولياء الأمور.",
              'HOW_TO_GET_STARTED': "للبدء، انقر على 'تسجيل' وأنشئ حسابك. اختر دورك (طالب، ولي أمر، أو معلم) واتبع الخطوات. بمجرد التسجيل، يمكنك الوصول إلى لوحة التحكم الشخصية.",
              'WHAT_SERVICES_OFFER': "معارفتي توفر: 📘 بوابات تعلم الطلاب، 👨‍👩‍👧 لوحات تحكم أولياء الأمور، 🏫 أدوات إدارة المدرسة، و 📊 أنظمة تتبع أكاديمي. كلها مصممة لتحسين تجربة التعلم.",
              'HOW_CREATE_ACCOUNT': "انقر على زر 'إنشاء حساب'، املأ تفاصيلك واختر دورك. يستغرق الأمر بضع دقائق فقط لإعداد ملفك الشخصي والوصول إلى المنصة.",
              'HOW_CONTACT_SUPPORT': "فريق الدعم لدينا جاهز لمساعدتك عبر: 📧 البريد الإلكتروني: support@maarefati.com ☎️ الهاتف: +216 92 329 007 📄 النموذج عبر الإنترنت على maarefati.com/contact.",
              'WHAT_LANGUAGES_SUPPORTED': "معارفتي تدعم واجهات العربية والفرنسية والإنجليزية لتسهيل الاستخدام للجميع.",
              'WHO_CAN_USE_MAAREFATI': "المنصة مصممة لـ: 👩‍🏫 المعلمين (لإدارة الفصول)، 👨‍👩‍👧 أولياء الأمور (لمتابعة تقدم الأطفال)، و 👦 الطلاب (للوصول إلى موارد التعلم).",
              'MOBILE_ACCESS': "نعم! معارفتي متوافقة مع الهاتف المحمول ويمكن الوصول إليها على الهواتف الذكية والأجهزة اللوحية وأجهزة الكمبيوتر—في أي وقت، في أي مكان.",
              'WHY_CHOOSE_MAAREFATI': "لأنه يوفر الوقت ⏳، ويحسن التواصل 📢، ويدعم الطلاب 📚، ويعطي المدارس أدوات إدارة رقمية حديثة 🏫.",
              'HOW_JOIN_FAMILY': "للانضمام إلى معارفتي، تحتاج إلى طلب رمز وصول (رمز 6 أرقام). يمكنك الحصول عليه عبر: 📧 إرسال بريد إلكتروني إلى ecolepriveeelfarabi@gmail.com ☎️ الاتصال بالمدرسة على +216 92 329 007 🏫 زيارة المؤسسة المدرسية مباشرة. بمجرد حصولك على الرمز، يمكنك المتابعة مع التسجيل.",
              'HOW_REGISTER': "📝 خطوات التسجيل:\n1️⃣ تحقق من بريدك الإلكتروني للحصول على رمز الوصول المكون من 6 أرقام.\n2️⃣ انسخ الرمز والصقه في نموذج التسجيل على معارفتي.\n3️⃣ أكمل إجراء التسجيل.\n4️⃣ ستتلقى بريد إلكتروني تأكيدي.\n✅ مرحباً بك في مجتمع معارفتي!",
              'HOW_DOWNLOAD_APP': "📲 يمكن تثبيت معارفتي كتطبيق ويب (PWA). الخطوات:\n- افتح Google Chrome (Android) أو Safari (iOS).\n- زر maarefati.com.\n- اختر **إضافة إلى الشاشة الرئيسية** من خيارات المتصفح.\nستحصل الآن على أيقونة التطبيق على هاتفك مثل أي تطبيق آخر."
            }
          }
          
          return translations[language] || translations['en']
        }

        // Use preLoginResponses for pre-login users
        const translatedResponses = getTranslatedResponses(params.language)
        const preLoginResponses = [
          {
            id: 'pre-login-1',
            question: 'Main partners',
            answer: translatedResponses['MAIN_PARTNERS'],
            keywords: ['basic info', 'school', 'location', 'address', 'where'],
            category: 'pre-login',
            language: params.language,
            isActive: true,
            priority: 10
          },
          {
            id: 'pre-login-2',
            question: 'What is Maarefati?',
            answer: translatedResponses['WHAT_IS_MAAREFATI'],
            keywords: ['what is maarefati', 'platform', 'digital', 'education', 'learning'],
            category: 'pre-login',
            language: params.language,
            isActive: true,
            priority: 9
          },
          {
            id: 'pre-login-3',
            question: 'How do I get started?',
            answer: translatedResponses['HOW_TO_GET_STARTED'],
            keywords: ['get started', 'how to begin', 'register', 'signup', 'start'],
            category: 'pre-login',
            language: params.language,
            isActive: true,
            priority: 8
          },
          {
            id: 'pre-login-4',
            question: 'What services do you offer?',
            answer: translatedResponses['WHAT_SERVICES_OFFER'],
            keywords: ['services', 'features', 'what do you offer', 'capabilities'],
            category: 'pre-login',
            language: params.language,
            isActive: true,
            priority: 7
          },
          {
            id: 'pre-login-5',
            question: 'How can I create an account?',
            answer: translatedResponses['HOW_CREATE_ACCOUNT'],
            keywords: ['create account', 'signup', 'register', 'open account'],
            category: 'pre-login',
            language: params.language,
            isActive: true,
            priority: 6
          },
          {
            id: 'pre-login-6',
            question: 'How can I contact support?',
            answer: translatedResponses['HOW_CONTACT_SUPPORT'],
            keywords: ['support', 'contact', 'help', 'assistance', 'help desk'],
            category: 'pre-login',
            language: params.language,
            isActive: true,
            priority: 5
          },
          {
            id: 'pre-login-7',
            question: 'What languages are supported?',
            answer: translatedResponses['WHAT_LANGUAGES_SUPPORTED'],
            keywords: ['languages', 'arabic', 'french', 'english', 'multilingual'],
            category: 'pre-login',
            language: params.language,
            isActive: true,
            priority: 4
          },
          {
            id: 'pre-login-8',
            question: 'Who can use Maarefati?',
            answer: translatedResponses['WHO_CAN_USE_MAAREFATI'],
            keywords: ['who can use', 'roles', 'teacher', 'parent', 'student'],
            category: 'pre-login',
            language: params.language,
            isActive: true,
            priority: 3
          },
          {
            id: 'pre-login-9',
            question: 'Do you have mobile access?',
            answer: translatedResponses['MOBILE_ACCESS'],
            keywords: ['mobile', 'phone', 'tablet', 'app', 'responsive'],
            category: 'pre-login',
            language: params.language,
            isActive: true,
            priority: 2
          },
          {
            id: 'pre-login-10',
            question: 'Why choose Maarefati?',
            answer: translatedResponses['WHY_CHOOSE_MAAREFATI'],
            keywords: ['why choose', 'benefits', 'advantages', 'why'],
            category: 'pre-login',
            language: params.language,
            isActive: true,
            priority: 1
          },
          {
            id: 'pre-login-11',
            question: 'How can I join the Maarefati family?',
            answer: translatedResponses['HOW_JOIN_FAMILY'],
            keywords: ['join', 'family', 'maarefati', 'access', 'token', 'code'],
            category: 'pre-login',
            language: params.language,
            isActive: true,
            priority: 10
          },
          {
            id: 'pre-login-12',
            question: 'How do I register on Maarefati?',
            answer: translatedResponses['HOW_REGISTER'],
            keywords: ['register', 'registration', 'sign up', 'signup', 'account', 'code', 'email'],
            category: 'pre-login',
            language: params.language,
            isActive: true,
            priority: 9
          },
          {
            id: 'pre-login-13',
            question: 'How can I download the Maarefati app?',
            answer: translatedResponses['HOW_DOWNLOAD_APP'],
            keywords: ['download', 'app', 'install', 'android', 'ios', 'pwa', 'mobile'],
            category: 'pre-login',
            language: params.language,
            isActive: true,
            priority: 8
          }
        ];
        
        
        
        const transformedResponse = {
          success: true,
          data: {
            responses: preLoginResponses
          }
        }
        
        return transformedResponse
      }
      
      // Use the regular responses endpoint with isActive=true filter
      const queryParams = new URLSearchParams()
      
      // Only append parameters if they have values (like the main function)
      if (params.category && params.category.trim()) queryParams.append('category', params.category)
      if (params.language && params.language.trim()) queryParams.append('language', params.language)
      if (params.userRole && params.userRole.trim()) queryParams.append('userRole', params.userRole)
      
      queryParams.append('isActive', 'true') // Only get active responses
      queryParams.append('pageNumber', '1')
      queryParams.append('pageCount', '100') // Get more responses
      queryParams.append('sortField', 'priority')
      queryParams.append('sortOrder', 'desc')

      const url = `${API_URL}/api/chatbot/responses?${queryParams}`
      const response = await circuitBreaker.execute(async () => {
        return await axios.get(url)
      })
      return {
        success: response.data.success,
        data: {
          responses: response.data.data?.responses || []
        }
      }
    } catch (error: any) {
      if (DEBUG_CHATBOT) {
        console.error('Error fetching active chatbot responses:', error)
      }
      // Handle 403 Forbidden error gracefully
      if (error.response?.status === 403) {
        return {
          success: true,
          data: {
            responses: []
          }
        }
      }
      
      if (error.response?.status === 500) {
        return {
          success: true,
          data: {
            responses: [],
            fallback: true
          }
        }
      }
      
      if (!error.response) {
        return {
          success: true,
          data: {
            responses: []
          }
        }
      }
      
      if (error.response?.status && error.response.status >= 500) {
        return {
          success: true,
          data: {
            responses: []
          }
        }
      }
      
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch active chatbot responses'
      )
    }
  }
)

// Create new chatbot response
export const createChatbotResponse = createAsyncThunk<
  CreateChatbotResponseResponse,
  CreateChatbotResponseRequest
>(
  'chatbot/createResponse',
  async (data, {rejectWithValue}) => {
    try {
      // Create data according to backend API specification
      const apiData: any = {
        question: data.question,
        answer: data.answer,
        keywords: data.keywords,
        category: data.category,
        language: data.language,
        priority: data.priority,
        isActive: data.isActive,
        userRoles: data.category === 'post-login' 
          ? (data.userRoles && data.userRoles.length > 0 ? data.userRoles : ['2'])
          : []
      }
      
      // Add establishement if available (as per API docs)
      if (data.establishement) {
        // Try sending as string ID first (as per API docs)
        apiData.establishement = data.establishement
      }
      
      try {
        const response = await axios.post(`${API_URL}/api/chatbot/responses`, apiData)
        return response.data
      } catch (error: any) {
        // Try without establishment field
        const apiDataWithoutEstablishment = { ...apiData }
        delete apiDataWithoutEstablishment.establishement
        
        const response = await axios.post(`${API_URL}/api/chatbot/responses`, apiDataWithoutEstablishment)
        return response.data
      }
    } catch (error: any) {
      console.error('Error creating chatbot response:', error)
      console.error('Error response:', error.response?.data)
      console.error('Error status:', error.response?.status)
      console.error('Error details:', error.response?.data?.error)
      console.error('Full error response:', JSON.stringify(error.response?.data, null, 2))
      console.error('Error message details:', error.response?.data?.error?.message)
      return rejectWithValue(
        error.response?.data?.error?.message || error.response?.data?.message || 'Failed to create chatbot response'
      )
    }
  }
)

// Update chatbot response
export const updateChatbotResponse = createAsyncThunk<
  UpdateChatbotResponseResponse,
  UpdateChatbotResponseRequest
>(
  'chatbot/updateResponse',
  async (data, {rejectWithValue}) => {
    try {
      const {id, ...updateData} = data
      const response = await axios.put(`${API_URL}/api/chatbot/responses/${id}`, updateData)
      return response.data
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to update chatbot response'
      )
    }
  }
)

// Delete chatbot response
export const deleteChatbotResponse = createAsyncThunk<
  DeleteChatbotResponseResponse,
  DeleteChatbotResponseRequest
>(
  'chatbot/deleteResponse',
  async (data, {rejectWithValue}) => {
    try {
      // Use the correct backend endpoint for delete
      const response = await axios.post(`${API_URL}/api/chatbot/delete`, { id: data.id })
      return response.data
    } catch (error: any) {
      console.error('Error deleting chatbot response:', error)
      console.error('Error response:', error.response?.data)
      return rejectWithValue(
        error.response?.data?.message || 'Failed to delete chatbot response'
      )
    }
  }
)

// Toggle chatbot response status
export const toggleChatbotResponseStatus = createAsyncThunk<
  ToggleChatbotResponseStatusResponse,
  ToggleChatbotResponseStatusRequest
>(
  'chatbot/toggleStatus',
  async (data, {rejectWithValue}) => {
    try {
      // Use the correct backend endpoint for toggle status
      const response = await axios.post(`${API_URL}/api/chatbot/toggle-status`, { 
        id: data.id, 
        isActive: data.isActive 
      })
      return response.data
    } catch (error: any) {
      console.error('Error toggling chatbot response status:', error)
      console.error('Error response:', error.response?.data)
      return rejectWithValue(
        error.response?.data?.message || 'Failed to toggle chatbot response status'
      )
    }
  }
)

// Search chatbot responses
export const searchChatbotResponses = createAsyncThunk<
  SearchChatbotResponsesResponse,
  SearchChatbotResponsesRequest
>(
  'chatbot/searchResponses',
  async (data, {rejectWithValue}) => {
    try {
      const response = await axios.post(`${API_URL}/api/chatbot/search`, data)
      return response.data
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to search chatbot responses'
      )
    }
  }
)
